import classNames from 'classnames';
import { kebabCase } from 'lodash';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CollegeDistributeDistributionReason from 'src/components/CollegeDistribute/CollegeDistributeDistributionReason/CollegeDistributeDistributionReason';
import CollegeDistributeSelectPayee from 'src/components/CollegeDistribute/CollegeDistributeSelectPayee/CollegeDistributeSelectPayee';
import CollegeDistributeStudentInformation from 'src/components/CollegeDistribute/CollegeDistributeStudentInfo/CollegeDistributeStudentInformation';
import DeductionList from 'src/components/DeductionList/DeductionList';
import { Address } from 'src/components/Exchange/types';
import BankAssociableText from 'src/components/Shared/BankAssociableText/BankAssociableText';
import CostBasisToolTip from 'src/components/Shared/CostBasisToolTip/CostBasisToolTip';
import ExpandCollapse from 'src/components/Shared/ExpandCollapse/ExpandCollapse';
import If from 'src/components/Shared/If/If';
import MarketVolatilityNote from 'src/components/Buy/BuyFlow/ExpandedFundList/MarketVolatilityNote';
import MultiInput from 'src/components/Shared/MultiInput/MultiInput';
import config from 'src/config/config';
import { updateFederalWithholding } from 'src/redux/modules/sellTransaction/actions';
import {
  getMandatoryWithholdingStates,
  getNonMandatoryWithholdingStates,
  getUserStateWithholdingRules,
} from 'src/redux/modules/stateWithholding/selectors';
import {
  APP_MINIMUM_BALANCE,
  DELIVERY_MAIL_CHECK,
  DELIVERY_MAIL_CHECK_TO_SCHOOL,
  UNIT,
} from 'src/settings/settings';
import { sellFundInputCloseoutWatcher } from 'src/utils/funds/funds-utility';
import { openExternalLinkInPane } from 'src/utils/link-helper';
import {
  isAppSell,
  isCollegeDistribute,
  isQCD,
} from 'src/utils/transactions-util';
import CostBasisSnapshot from '../CostBasisSnapshot/CostBasisSnapshot';
import SellAllShares from '../SellAllShares/SellAllShares';
import styles from './DistributionAmount.module.scss';
import EarlyWithdrawalPenalty from '../../Shared/EarlyWithdrawalPenalty/EarlyWithdrawalPenalty';
import NonCoveredShares from './NonCoveredShares/NonCoveredShares';
import WhatWillIPayInTaxes from './WhatWillIPayInTaxes';
import '../../Buy/BuyFlow/ExpandedFundList//MarketVolatilityNote.scss';
import StateIncomeTaxDisclosure from '../StateIncomeTaxDisclosure/StateIncomeTaxDisclosure';
import IraWithholdingWarning from '../IraWithholdingWarning/IraWithholdingWarning';
import { getStudentDetails } from 'src/redux/modules/studentDetail';
import {
  getRasDailyMax,
  isAPP,
  isRAS,
} from '../../../utils/advice/advice-utility';
import { getSellPendingTransactions } from '../../../redux/modules/pendingTransactions/pendingTransactions.selectors';

export interface DistributionAmountProps {
  customerWebViewEnabled: boolean;
  isSalesforceRep: boolean;
  actions;
  deliveryMethod;
  fund;
  marketVolatility;
  RMData;
  showHeader?: boolean;
  isDisplayingRMDAmount?: boolean;
  transactionLimit;
  userAddress: Address;
  financialAdvisor?;
}

const DistributionAmount: React.VFC<DistributionAmountProps> = ({
  customerWebViewEnabled,
  isSalesforceRep,
  actions,
  deliveryMethod,
  fund,
  marketVolatility,
  RMData,
  isDisplayingRMDAmount,
  showHeader = false,
  transactionLimit,
  financialAdvisor,
}) => {
  const dispatch = useDispatch();
  const mandatoryWithholdingStates = useSelector(getMandatoryWithholdingStates);
  const nonMandatoryWithholdingStates = useSelector(
    getNonMandatoryWithholdingStates
  );
  const userStateWithholdingRules = useSelector(getUserStateWithholdingRules);
  const sellPendingTransactions = useSelector(getSellPendingTransactions);
  const studentDetail = useSelector(getStudentDetails);
  const {
    openModal,
    updateSellFund,
    updateSellFundAmount,
    updateSellFundDistributionDisclaimer,
    updateSellFundDistributionPayee,
    updateSellFundDistributionReason,
    updateSellFundUnit,
    updateQCDFundAmount,
  } = actions;
  const {
    agreementID,
    address,
    closeout,
    distributionPayeeDisclaimerAccepted,
    distributionReason,
    fundLimitMessage,
    grossAmount,
    grossAmountDollars,
    grossAmountError,
    grossUnit,
    isFundAssociable,
    hasEarlyWithdrawalPenalty,
    hasUncollectedShares,
    inFocus,
    nonRetirementLike,
    payee,
    selected,
    ticker,
    federalWithholdingApplied,
    federalWithholdingDollars,
    federalWithholdingPercent,
    stateIsEstimate,
    stateWithholdingApplied,
    stateWithholdingDisabled,
    stateWithholdingDollars,
    stateWithholdingPercent,
    formattedEntityNames,
    agreementParties,
    productType,
  } = fund;
  const marketolatilityNoteBox = 'market-volatility-note-box';
  const marketVolatilityNote = 'market-volatility-note';
  const isMandatoryStateTax =
    fund?.stateWithholdingApplied || fund?.stateWithholdingDisabled;
  const taxableDetails = [
    {
      className: 'collapse1',
      heading: 'How are Noncovered Shares redeemed?',
      show: true,
      content: (
        <NonCoveredShares
          coveredShares={fund.coveredShares}
          uncoveredShares={fund.uncoveredShares}
        />
      ),
    },
    {
      className: 'collapse2',
      heading: 'What will I pay in taxes?',
      show: nonRetirementLike && !(!customerWebViewEnabled && isSalesforceRep),
      content: <WhatWillIPayInTaxes />,
    },
  ];
  if (isAppSell() && isRAS(productType)) {
    transactionLimit.maximum = getRasDailyMax(fund, sellPendingTransactions);
    transactionLimit.minimum = 1;
  }
  const fundState = fund.address.state;
  return (
    <div
      className={classNames(
        'distribution-amount-container',
        styles['container']
      )}
    >
      <If test={showHeader}>
        <div className="mobile-only-amount">Enter an Amount</div>
        <div className={styles['desktop-only']}>
          <h3 className="hidden-xs">Distribution Amount</h3>
        </div>
        <>
          {isQCD() && !!fund.QCDgrossAmountError && (
            <>{fund.QCDgrossAmountError}</>
          )}
        </>
      </If>
      <div
        className={classNames(
          styles['section'],
          `e2e-fund-sell-editor-${kebabCase(ticker)}`
        )}
      >
        <div
          className={`${styles['input-deduction-section']} input-deduction-section`}
        >
          <div className={`${styles['input-label']} input-label`}>
            {!isQCD() && <>Enter an Amount</>}
            {isQCD() && (
              <>
                Amount<div className={`${styles['min']} min`}>Min: $1</div>
                {!!fund.QCDgrossAmountError && fund.QCDgrossAmountError}
              </>
            )}
          </div>
          <div className={styles['flex-container-row']}>
            <MultiInput
              allowedUnits={isAppSell() || isQCD() ? [UNIT.DOLLARS] : undefined}
              applyBranding={false}
              ariaLabel={'Distribution Amount'}
              autoFocus={selected}
              className={classNames(
                styles['distribution-amount-amount-input'],
                `e2e-input-field-${kebabCase(ticker)}`
              )}
              defaultValue={closeout ? undefined : String(grossAmount)}
              disabled={
                closeout ||
                (isAPP(productType) &&
                  fund.balanceAsOf > 0 &&
                  fund.balanceAsOf <= APP_MINIMUM_BALANCE)
              }
              displayErrorMessage={grossAmountError}
              displayValidationMessage={!grossAmountError}
              error={grossAmountError}
              errorClassName={`e2e-input-error e2e-input-error-${kebabCase(
                ticker
              )}`}
              id={agreementID}
              inputID={`amt-${agreementID}`}
              selectedInput={closeout ? UNIT.SHARES : grossUnit}
              setFocus={inFocus}
              validationMessage={fundLimitMessage}
              onBlur={() => {
                updateSellFund({
                  agreementID,
                  inFocus: undefined,
                });
              }}
              onInputChange={(amount) => {
                updateSellFundAmount({ agreementID, amount });
                if (isQCD()) {
                  dispatch(updateQCDFundAmount({ fund, amount }));
                }
                sellFundInputCloseoutWatcher(
                  amount,
                  fund,
                  openModal,
                  transactionLimit,
                  updateSellFund
                );
              }}
              onUnitChange={(unit: UNIT) =>
                updateSellFundUnit({ agreementID, unit })
              }
            />
            <If test={!hasUncollectedShares && !isRAS(productType) && !isQCD()}>
              <SellAllShares
                fund={fund}
                grossAmountError={grossAmountError}
                openModal={openModal}
                updateSellFund={updateSellFund}
              />
            </If>
          </div>
          <If test={isCollegeDistribute()}>
            <If test={deliveryMethod === DELIVERY_MAIL_CHECK_TO_SCHOOL}>
              <CollegeDistributeStudentInformation
                agreementID={agreementID}
                agreementParties={agreementParties}
                formattedEntityNames={formattedEntityNames}
                fund={fund}
                studentDetail={studentDetail}
              />
            </If>
            <If test={deliveryMethod !== DELIVERY_MAIL_CHECK_TO_SCHOOL}>
              <CollegeDistributeSelectPayee
                agreementID={agreementID}
                dispatch={dispatch}
                distributionPayeeDisclaimerAccepted={
                  distributionPayeeDisclaimerAccepted
                }
                fund={fund}
                payee={payee}
                showPayeeDisclaimer={deliveryMethod !== DELIVERY_MAIL_CHECK}
                updateDistributionPayee={updateSellFundDistributionPayee}
                updateDistributionPayeeDisclaimer={
                  updateSellFundDistributionDisclaimer
                }
              />
              <CollegeDistributeDistributionReason
                agreementID={agreementID}
                distributionReason={distributionReason}
                updateDistributionReason={updateSellFundDistributionReason}
              />
            </If>
          </If>
          {!isQCD() && (
            <DeductionList
              showTransactionFees
              customerWebViewEnabled={customerWebViewEnabled}
              federalWithholdingApplied={federalWithholdingApplied ?? false}
              isRetirementLike={!fund?.nonRetirementLike}
              isSalesforceRep={isSalesforceRep}
              showCloseoutFee={closeout && fund.closeoutFee > 0}
              showMandatoryStateTaxes={isMandatoryStateTax}
              showNonMandatoryStateTaxes={!isMandatoryStateTax}
              userState={fundState}
              withholdingEditAction={updateFederalWithholding}
              {...{
                grossAmountDollars,
                federalWithholdingDollars,
                federalWithholdingPercent,
                fund,
                mandatoryWithholdingStates,
                marketVolatility,
                RMData,
                stateIsEstimate,
                stateWithholdingApplied,
                stateWithholdingDisabled,
                stateWithholdingDollars,
                stateWithholdingPercent,
                financialAdvisor,
              }}
            >
              Total Take-Home Amount
            </DeductionList>
          )}
          {isQCD() && <MarketVolatilityNote />}
          <BankAssociableText
            containerClass="retirement"
            showText={
              isFundAssociable && deliveryMethod !== DELIVERY_MAIL_CHECK
            }
          />
          {!customerWebViewEnabled && isSalesforceRep && (
            <span>
              <If test={!fund?.nonRetirementLike && !isQCD()}>
                <div
                  className={
                    !customerWebViewEnabled && isSalesforceRep
                      ? marketolatilityNoteBox
                      : ''
                  }
                >
                  <div
                    className={
                      !customerWebViewEnabled && isSalesforceRep
                        ? marketVolatilityNote
                        : ''
                    }
                  >
                    <IraWithholdingWarning
                      caller={null}
                      closeIraWithholdingWarning={null}
                      isSalesforceRepMessage={true}
                      setInputFocus={null}
                    />
                  </div>
                </div>
              </If>
              <If test={!fund?.nonRetirementLike && !isQCD()}>
                <div
                  className={
                    !customerWebViewEnabled && isSalesforceRep
                      ? marketolatilityNoteBox
                      : ''
                  }
                >
                  <div
                    className={
                      !customerWebViewEnabled && isSalesforceRep
                        ? marketVolatilityNote
                        : ''
                    }
                  >
                    <StateIncomeTaxDisclosure
                      closeStateIncomeTaxDisclosure={null}
                      isSalesforceRepMessage={true}
                      mandatoryWithholdingStates={mandatoryWithholdingStates}
                      nonMandatoryWithholdingStates={
                        nonMandatoryWithholdingStates
                      }
                      state={address.state}
                      stateWithholdingRules={userStateWithholdingRules}
                    />
                  </div>
                </div>
              </If>
              <If test={fund.hasRecurring}>
                <div
                  className={
                    !customerWebViewEnabled && isSalesforceRep
                      ? marketolatilityNoteBox
                      : ''
                  }
                >
                  <div
                    className={
                      !customerWebViewEnabled && isSalesforceRep
                        ? marketVolatilityNote
                        : ''
                    }
                  >
                    A recurring transaction is linked to this fund. Check with
                    the client to ensure existing instructions do not need to be
                    amended. To edit or remove this instruction, close this tool
                    and visit Automatic Transactions flow.
                  </div>
                </div>
              </If>
            </span>
          )}
        </div>
        <div className={styles['right-panel']}>
          {hasEarlyWithdrawalPenalty && (
            <EarlyWithdrawalPenalty ticker={ticker} />
          )}

          <If test={nonRetirementLike && !isCollegeDistribute()}>
            <CostBasisSnapshot
              changeAction={() =>
                openExternalLinkInPane(
                  `${config.url}/usis/my-accounts/profile/cost-basis`
                )
              }
              customerWebViewEnabled={customerWebViewEnabled}
              fund={fund}
              isSalesforceRep={isSalesforceRep}
              labelComponent={
                <CostBasisToolTip
                  id={'tooltip_cost_basis' + fund.agreementID}
                  styles={styles}
                />
              }
            />
            <ExpandCollapse details={taxableDetails} />
          </If>
        </div>
      </div>
    </div>
  );
};

export default DistributionAmount;
